import { useId, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  methodLabel,
  pickPaymentDetails,
  usePaymentRegister,
  useUpdatePaymentDetails,
  type PaymentRegisterRow,
} from "@/api/payments";
import { RupeeValue } from "@/components/forms/RupeeValue";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { PageHeader } from "@/components/ui/page-header";
import { formatDate, formatInr, formatMoney } from "@/lib/format";
import { totalsByCurrency } from "@/lib/money";
import { useFormValues, useSubmitState } from "@/lib/use-form";

/** As reports/income.py rupees_received (and the books' payment entry): the eFIRA figure, else
 *  net at the payment's own rate. */
function rupeeValue(payment: PaymentRegisterRow): string | null {
  if (payment.inr_credited) return payment.inr_credited;
  if (payment.currency === "INR") return payment.net_amount;
  if (!payment.fx_rate) return null;
  return (Math.round(Number(payment.net_amount) * 100 * Number(payment.fx_rate)) / 100).toFixed(2);
}

/** Fill in the rupee value of a recorded payment; its other bank details are kept as they are. */
function RupeeValueForm({
  payment,
  onDone,
  onReload,
}: {
  payment: PaymentRegisterRow;
  onDone: () => void;
  onReload: () => void;
}) {
  const id = useId();
  const update = useUpdatePaymentDetails(payment.invoice_id);
  const { values, set, setValues } = useFormValues({ fx_rate: "", inr_credited: "" });
  const { error, errors, saving, run } = useSubmitState();

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    void run(async () => {
      await update.mutateAsync({
        id: payment.id,
        input: {
          ...pickPaymentDetails(payment),
          fx_rate: values.fx_rate.trim() || null,
          inr_credited: values.inr_credited.trim() || null,
        },
        version: payment.version,
      });
      onDone();
    });
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="mt-3 flex flex-col gap-4 rounded-md border p-4">
      <RequestError error={error} onReload={onReload} />
      <RupeeValue
        id={`${id}-rupees`}
        currency={payment.currency}
        net={payment.net_amount}
        receivedOn={payment.received_on}
        receivedCurrency={payment.received_currency ?? ""}
        receivedRate={payment.received_rate ? String(Number(payment.received_rate)) : ""}
        receivedRateFixed
        fxRate={values.fx_rate}
        inrCredited={values.inr_credited}
        error={errors.fx_rate}
        onChange={(rates) =>
          setValues((current) => ({ ...current, fx_rate: rates.fx_rate ?? current.fx_rate }))
        }
      />
      <TextField
        id={`${id}-inr`}
        label="INR credited"
        inputMode="decimal"
        value={values.inr_credited}
        onChange={(value) => set("inr_credited", value)}
        error={errors.inr_credited}
        hint="What the bank credited, as on the eFIRA; optional, and counts instead of the rate"
        className="md:max-w-xs"
      />
      <FormActions
        saving={saving}
        submitLabel="Save rupee value"
        onCancel={onDone}
        disabled={!values.fx_rate.trim() && !values.inr_credited.trim()}
      />
    </form>
  );
}

/** Payments with no rupee figure: Payouts due cannot share them out until they have one. */
function WaitingForRupees() {
  const waiting = usePaymentRegister({ withoutRupees: true, limit: 500 });
  const [editing, setEditing] = useState<string | null>(null);
  if (waiting.isError) return <RequestError error={waiting.error} />;
  const rows = waiting.data ?? [];
  if (rows.length === 0) return null;
  return (
    <Card className="border-amber-300">
      <CardHeader>
        <CardTitle>Waiting for a rupee value</CardTitle>
        <CardDescription>
          {rows.length === 1 ? "This payment has" : `These ${rows.length} payments have`} no rupee
          figure yet, so Money → Payouts due cannot pay the pools and the team from{" "}
          {rows.length === 1 ? "it" : "them"}. Add the rate for the day the money arrived: the
          books value the payment at it too, and Books → Exchange rates is only the fallback for a
          payment without one.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y">
          {rows.map((payment) => (
            <li key={payment.id} className="py-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm">
                  <strong>{formatMoney(payment.amount, payment.currency)}</strong>
                  {payment.received_currency && payment.received_amount
                    ? ` (${formatMoney(payment.received_amount, payment.received_currency)})`
                    : ""}{" "}
                  · {formatDate(payment.received_on)} ·{" "}
                  <Link to={`/invoices/${payment.invoice_id}`} className="text-primary hover:underline">
                    {payment.invoice_no}
                  </Link>{" "}
                  · {payment.client_code}
                  {payment.vessel_name ? ` · ${payment.vessel_name}` : ""} ·{" "}
                  <span className="text-muted-foreground">{methodLabel(payment.method)}</span>
                </span>
                {editing !== payment.id && (
                  <Button size="sm" variant="outline" onClick={() => setEditing(payment.id)}>
                    Add rupee value
                  </Button>
                )}
              </div>
              {editing === payment.id && (
                <RupeeValueForm
                  key={payment.version}
                  payment={payment}
                  onDone={() => setEditing(null)}
                  onReload={() => void waiting.refetch()}
                />
              )}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export function PaymentsPage() {
  const [receivedFrom, setReceivedFrom] = useState("");
  const [receivedTo, setReceivedTo] = useState("");
  const [includeVoided, setIncludeVoided] = useState(false);
  const payments = usePaymentRegister({
    receivedFrom: receivedFrom || undefined,
    receivedTo: receivedTo || undefined,
    includeVoided,
  });

  const counted = (payments.data ?? []).filter((payment) => payment.voided_at === null);
  const received = totalsByCurrency(counted, (p) => p.currency, (p) => p.amount);
  const inrCents = counted.reduce(
    (sum, payment) => sum + Math.round(Number(payment.inr_credited ?? 0) * 100),
    0,
  );
  const pendingFira = counted.filter((payment) => !payment.fira_ref).length;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Payments"
        description="Money received against invoices, with the INR credited and eFIRA references. Rupees are the INR credited, else the net at the payment's own rate: what the books and Payouts due use."
      />
      <WaitingForRupees />
      <div className="grid gap-3 sm:grid-cols-[auto_auto_1fr] sm:items-end sm:gap-4">
        <TextField
          id="payments-from"
          label="Received from"
          type="date"
          value={receivedFrom}
          onChange={setReceivedFrom}
        />
        <TextField
          id="payments-to"
          label="Received to"
          type="date"
          value={receivedTo}
          onChange={setReceivedTo}
        />
        <Checkbox
          id="payments-voided"
          label="Show voided payments"
          checked={includeVoided}
          onChange={setIncludeVoided}
          className="sm:pb-2"
        />
      </div>

      {payments.isPending ? (
        <p className="text-muted-foreground">Loading payments…</p>
      ) : payments.isError ? (
        <RequestError error={payments.error} />
      ) : payments.data.length === 0 ? (
        <EmptyState
          title="No payments in this period"
          description="Record payments from the invoice they settle."
        />
      ) : (
        <>
          <p className="text-sm">
            Received{" "}
            <strong>
              {received.map((total) => formatMoney(total.amount, total.currency)).join(" + ")}
            </strong>
            {inrCents > 0 && (
              <>
                {" "}
                · credited <strong>{formatInr((inrCents / 100).toFixed(2))}</strong>
              </>
            )}
            {pendingFira > 0 && (
              <span className="text-muted-foreground"> · {pendingFira} without an eFIRA number</span>
            )}
          </p>
          <ul className="flex flex-col gap-3 md:hidden">
            {payments.data.map((payment) => (
              <li key={payment.id}>
                <Link
                  to={`/invoices/${payment.invoice_id}`}
                  className="flex flex-col gap-1 rounded-lg border bg-card p-4 shadow-sm"
                >
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-semibold">{formatMoney(payment.amount, payment.currency)}</span>
                    {payment.voided_at && <Badge variant="destructive">Voided</Badge>}
                  </span>
                  <span className="text-sm text-primary">
                    {payment.invoice_no} · {payment.client_code}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(payment.received_on)}
                    {payment.inr_credited ? ` · ${formatInr(payment.inr_credited)}` : ""}
                    {payment.fira_ref ? ` · eFIRA ${payment.fira_ref}` : " · eFIRA pending"}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          <div className="hidden table-frame md:block">
            <table className="data-table">
              <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">Received</th>
                  <th className="px-4 py-2 font-medium">Invoice</th>
                  <th className="px-4 py-2 font-medium">Client</th>
                  <th className="px-4 py-2 text-right font-medium">Amount</th>
                  <th className="px-4 py-2 text-right font-medium">Charges</th>
                  <th className="px-4 py-2 text-right font-medium">Rupees</th>
                  <th className="px-4 py-2 font-medium">eFIRA</th>
                  <th className="px-4 py-2">
                    <span className="sr-only">Status</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {payments.data.map((payment) => (
                  <tr key={payment.id} className="border-b last:border-0 hover:bg-accent/40">
                    <td className="whitespace-nowrap px-4 py-2">{formatDate(payment.received_on)}</td>
                    <td className="whitespace-nowrap px-4 py-2 font-medium">
                      <Link
                        to={`/invoices/${payment.invoice_id}`}
                        className="text-primary hover:underline"
                      >
                        {payment.invoice_no}
                      </Link>
                      {payment.vessel_name && (
                        <span className="block text-xs font-normal text-muted-foreground">
                          {payment.vessel_name}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">{payment.client_code}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                      {formatMoney(payment.amount, payment.currency)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                      {Number(payment.bank_charges) > 0
                        ? formatMoney(payment.bank_charges, payment.currency)
                        : "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">
                      {rupeeValue(payment) ? formatInr(rupeeValue(payment) ?? "") : "—"}
                      {!payment.inr_credited && payment.fx_rate && (
                        <span className="block text-xs text-muted-foreground">
                          at {String(Number(payment.fx_rate))}, not yet credited
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">{payment.fira_ref || "Pending"}</td>
                    <td className="px-4 py-2">
                      {payment.voided_at && <Badge variant="destructive">Voided</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
